import React, { useState, useEffect } from 'react';
import { X, Search, Check, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Tables } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';

interface BookTravelModalProps {
  isOpen: boolean;
  onClose: () => void;
  travelPackage: Tables<'travel_packages'>;
}

const BookTravelModal: React.FC<BookTravelModalProps> = ({ 
  isOpen, 
  onClose, 
  travelPackage 
}) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Tables<'contacts'>[]>([]);
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
    } else {
      // Reset state when modal closes
      setSelectedContact(null);
      setNotes('');
      setSearchQuery('');
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase.from('contacts').select('*');

      // Filter based on user role
      if (user?.role === 'pr') {
        query = query.eq('created_by', user.id);
      } else if (user?.role === 'organizer') {
        query = query.eq('city', user.city);
      }

      const { data, error: queryError } = await query;
      
      if (queryError) throw queryError;
      setContacts(data || []);
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      setError(err.message || 'Errore nel caricamento dei contatti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;

    try {
      setLoading(true);
      setError(null);

      const { error: bookingError } = await supabase
        .from('travel_bookings')
        .insert({
          travel_package_id: travelPackage.id,
          contact_id: selectedContact,
          notes,
          created_by: user?.id
        });

      if (bookingError) throw bookingError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Errore nella creazione della prenotazione');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.phone.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">Prenota Viaggio</h2>
            <p className="text-gray-600">{travelPackage.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        )}

        {success ? (
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Prenotazione Confermata!
            </h3>
            <p className="text-gray-600">
              La prenotazione è stata registrata con successo.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleziona Partecipante
              </label>
              
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Cerca partecipanti..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
                />
              </div>

              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-gray-500">
                    Caricamento contatti...
                  </div>
                ) : filteredContacts.length > 0 ? (
                  <div className="divide-y">
                    {filteredContacts.map((contact) => (
                      <label
                        key={contact.id}
                        className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                          selectedContact === contact.id ? 'bg-rose-50' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="contact"
                          value={contact.id}
                          checked={selectedContact === contact.id}
                          onChange={() => setSelectedContact(contact.id)}
                          className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300"
                        />
                        <div className="ml-3">
                          <p className="font-medium">{contact.name}</p>
                          <p className="text-sm text-gray-500">
                            {contact.email} • {contact.phone}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    Nessun contatto trovato
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (opzionale)
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                placeholder="Aggiungi eventuali note o richieste speciali..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={!selectedContact || loading}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Prenotazione in corso...' : 'Conferma Prenotazione'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookTravelModal;